import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Form, Button } from 'react-bootstrap'
import { connect } from 'react-redux'
import LoadingSpinner from '../../../components/LoadingSpinner'
import registerStyle from '../../../Views/Register/Register.module.scss'
import Checkbox from '../../../components/Checkbox'
import { useRouteMatch } from 'react-router-dom'
import ImageInput from '../../../components/ImageInput'
import ModalFooter from '../../Modal/ModalFooter'
import general from '../../../helpers/general'
import Modal from '../../Modal'
import IngredientForm from '../IngredientForm'
import { toast } from 'react-toastify'
import DietaryConsiderations from './DietaryConsiderations'
import SideDishesSelector from './SideDishesSelector'
import SelectList from '../../SelectList'
import GeneralModal from '../../../components/Modal/General'
import CustomizationForm from '../CustomizationForm'
import styles from './DietaryConsiderations.module.scss'
import DefaultCustomizationsData from '../../Forms/CustomizationForm/DefaultData'
import IngredientAttribution from '../../IngredientAttrubution/IngredientAttribution'
import File from '../../../utils/file'
import TextEditor from '../../TextEditor'

function DishForm({
	Category,
	Dish,
	Ingredient,
	Auth,
	Restaurant,
	update,
	dishID,
	closeModal,
	categoryID,
	Admin,
	dish,
}) {
	const { register, handleSubmit, errors, setValue, reset, getValues } = useForm({
		defaultValues: {
			description: '',
			sideDishes: JSON.stringify([]),
			customizations: JSON.stringify(DefaultCustomizationsData?.dish?.customizations()),
		},
	})
	const [loading, setLoading] = useState(false)
	const [imageSelected, setImageSelected] = useState(null)
	const [isAvailable, setIsAvailable] = useState(true)
	const [fileError, setFileError] = useState(null)
	const [error, setError] = useState(null)
	const [sideDishes, setSideDishes] = useState([])
	const [selectedSideDishes, setSelectedSideDishes] = useState([])
	const [priceError, setPriceError] = useState(null)
	const [selectedIngredients, setSelectedIngredients] = useState([])
	const [selectedCustomizations, setSelectedCustomizations] = useState([])

	const [dietaryResetKey, setDietaryResetKey] = useState(new Date().getTime().toString())
	const [editorValue, setEditorValue] = useState('')

	const maxFileSize = parseInt(process.env.REACT_APP_MAX_FILE_SIZE)
	const maxFileSizeErrorText = process.env.REACT_APP_MAX_FILE_SIZE_ERROR

	const [modal, setModal] = useState({
		dialogClassName: null,
		visible: false,
		Component: null,
		title: '',
		closeModal: () => setModal({ ...modal, visible: false }),
	})

	const resetFormAfterCreating = () => {
		reset()
		// Re-register virtual fields after reset since v6 loses them
		register({ name: 'description', value: '' })
		register({ name: 'sideDishes', value: JSON.stringify([]) })
		register({ name: 'customizations', value: JSON.stringify(DefaultCustomizationsData?.dish?.customizations()) })
		setImageSelected(null)
		setIsAvailable(true)
		setSideDishes([])
		setSelectedSideDishes([])
		setSelectedIngredients([])
		setSelectedCustomizations([])
		setEditorValue('')
		setDietaryResetKey(new Date().getTime().toString())
	}

	const { dishes } = Dish
	const { ingredients } = Ingredient
	const { restaurant } = Restaurant
	const { categories } = Category
	const match = useRouteMatch('/update-dish/:id')

	const syncCustomizationsField = (customizationsObj) => {
		const serialized = JSON.stringify(customizationsObj)
		register({ name: 'customizations', value: serialized })
		setValue('customizations', serialized)
	}

	const mergeSelectedCustomsIntoForm = (menuWideSelection) => {
		const base = general.parseJSON(getValues()?.customizations, DefaultCustomizationsData.dish.customizations())
		base.selected = (menuWideSelection || []).map((s) => s?._id || s?.value)
		syncCustomizationsField(base)
	}

	const commitDishCustomizationsFromModal = (obj) => {
		const payload = {
			...obj,
			selected: (selectedCustomizations || []).map((s) => s?._id || s?.value),
		}
		syncCustomizationsField(payload)
	}

	useEffect(() => {
		if (update) {
			/* Check dish ingredients */
			if (Array.isArray(dish?.ingredients)) {
				const selected = dish.ingredients
					.filter((ing) => ing && ing.ingredient)
					.map((ing) => ing.ingredient)
				setSelectedIngredients(selected)
			}

			if (dish?.image) {
				setImageSelected(dish?.image)
			}

			setEditorValue(dish?.description || '')

			/* Check customizations */
			if (dish?.customizations) {
				syncCustomizationsField(dish.customizations)
				if (Array.isArray(dish.customizations.selected)) {
					setSelectedCustomizations(dish.customizations.selected)
				} else {
					setSelectedCustomizations([])
				}
			} else {
				syncCustomizationsField(DefaultCustomizationsData?.dish?.customizations())
			}

			/* Check dish isAvailable */
			setIsAvailable(Boolean(dish?.isAvailable))

			/* Check side dishes */
			setSelectedSideDishes(dish?.sideDishes ? dish.sideDishes.map((d) => d._id) : [])
		} else {
			/* When creating dish set default customizations */
			syncCustomizationsField(DefaultCustomizationsData?.dish?.customizations())
		}
		//eslint-disable-next-line
	}, [dish])

	useEffect(() => {
		Ingredient?.load()
		Dish.load()
		Category.load()
		Restaurant?.find()
		register({ name: 'sideDishes', value: JSON.stringify(selectedSideDishes) })
		if (!update) {
			register({ name: 'customizations', value: JSON.stringify(DefaultCustomizationsData?.dish?.customizations()) })
		}
		register({ name: 'description', value: '' })
		//eslint-disable-next-line
	}, [])

	const transformCustomizations = (customizations) => {
		if (!Array.isArray(customizations) || customizations.length === 0) return []
		return customizations.map((customization) => {
			if ((customization?._id || customization?.name) && !customization?.value) {
				return { value: customization._id, label: customization.name }
			}
			return { value: customization?.value, label: customization?.label }
		})
	}

	const addIngredient = () => {
		setModal({
			...modal,
			title: 'Add missing ingredients',
			visible: true,
			dialogClassName: null,
			Component: (
				<IngredientForm
					handleDataOnDishForm={(data) => handleMissingIngredients(data)}
					closeModal={() => setModal({ ...modal, visible: false })}
					IngredientRedux={Ingredient}
				/>
			),
		})
	}

	const getDishId = () => {
		if (update && match?.params?.id) return match?.params?.id
		else if (dishID) return dishID
		return null
	}

	useEffect(() => {
		setValue('description', editorValue)
		// eslint-disable-next-line
	}, [editorValue])

	useEffect(() => {
		if (typeof dishes === 'object') setSideDishes(dishes.filter((d) => d._id?.toString() !== getDishId()))
		// eslint-disable-next-line
	}, [dishes])

	useEffect(() => {
		setValue('sideDishes', JSON.stringify(selectedSideDishes))
		// eslint-disable-next-line
	}, [selectedSideDishes])

	const validateFile = (file) => {
		if (!imageSelected || typeof imageSelected === 'string') {
			setFileError(null)
			return true
		}

		if (file?.size > maxFileSize) {
			setFileError(`This file ${file?.name} is big. Maximum size for file is ${maxFileSizeErrorText}.`)
			return false
		}

		try {
			const validExtensionsFile = File.allowed_extensions
			for (let i = 0; i < validExtensionsFile.length; i++) {
				if (file?.name?.endsWith(validExtensionsFile[i])) {
					setFileError(null)
					return true
				}
			}
			setFileError('File not allowed')
			return false
		} catch (err) {
			return false
		}
	}

	const generateFormData = (data) => {
		let ingredients = []

		if (selectedIngredients && selectedIngredients.length > 0) {
			ingredients = selectedIngredients.map((ingredient) => ({
				ingredient: ingredient?._id || ingredient?.value,
			}))
		}

		data.isAvailable = isAvailable
		if (categoryID) data['category'] = categoryID

		const formData = new FormData()
		Object.keys(data).forEach((key) => formData.append(key, data[key]))

		formData.append('image', imageSelected)
		formData.append('ingredients', JSON.stringify(ingredients))
		return formData
	}

	const showConfirmationModal = (data, message) => {
		setModal({
			...modal,
			title: 'Confirm modal',
			visible: true,
			confirmation: true,
			dialogClassName: null,
			Component: (
				<GeneralModal
					{...modal}
					customMessage={message}
					customButtonMessage="Continue anyway"
					toastMessage={update ? 'Dish updated successfully' : 'Dish created successfully'}
					funk={async () => {
						const response = update ? await Dish.update(data, dish?._id) : await Dish.create(data)
						if (response?.confirmation === 'Success') {
							setModal({ ...modal, visible: false })
							typeof closeModal === 'function' && closeModal()
							resetFormAfterCreating()
						} else {
							setModal({ ...modal, visible: false })
						}
						return response
					}}
					closeModal={() => setModal({ ...modal, visible: false })}
				/>
			),
		})
	}

	const submit = async (data) => {
		if (!validateFile(imageSelected)) return false

		if (data?.price) {
			const validatedPrice = general.validatePrice(data?.price)
			if (validatedPrice?.err) {
				setPriceError(validatedPrice?.message)
				document?.querySelector('.modal')?.scrollTo({ top: 0 })
				document?.querySelector('.panel-wrapper')?.scrollTo({ top: 0 })
				return false
			}
			data.price = validatedPrice.price
			setPriceError(null)
		}

		// TEMP
		if (dishes.find((dish) => dish.name === data.name) && !update) {
			const noIngredients = !selectedIngredients || selectedIngredients?.length === 0
			return showConfirmationModal(data, (
				<>
					<p>{`You already have an item named "${data?.name}"`}</p>
					{noIngredients && <p style={{ fontSize: 16 }}>You also did not select any ingredient(s).</p>}
					<p style={{ fontSize: 16 }}>Do you want to save/create this dish anyway?</p>
				</>
			))
		}

		data = generateFormData(data)

		if (!selectedIngredients || selectedIngredients?.length === 0) {
			return showConfirmationModal(data, (
				<>
					<p>You didn't select any ingredient(s).</p>
					<p style={{ fontSize: 16 }}>Do you want to save/create this dish anyway?</p>
				</>
			))
		}

		setLoading(true)

		const response = update ? await Dish.update(data, dish?._id) : await Dish.create(data, categoryID)
		if (response?.confirmation === 'Fail') {
			setError(response?.message)
			setLoading(false)
		} else {
			setError(null)
			toast(update ? 'Dish updated successfully' : 'Dish created successfully')
			setLoading(false)
			typeof closeModal === 'function' && closeModal()
			resetFormAfterCreating()
		}
	}

	const handleMissingIngredients = (ingredients) => {
		if (!Array.isArray(ingredients) || ingredients.length === 0) return
		const missedIngredients = ingredients.filter((ing) => ing?._id && ing?.name)
		if (missedIngredients.length > 0) {
			setSelectedIngredients([...selectedIngredients, ...missedIngredients])
		}
	}

	const parseCustomizations = (customizations) => {
		try {
			return JSON.parse(customizations)
		} catch (err) {
			return []
		}
	}

	const getCustomCustomizations = () => {
		try {
			const values = getValues()
			const customizations = parseCustomizations(values?.customizations)
			return customizations?.custom
		} catch (err) {
			return []
		}
	}

	const showCustomizationModal = () => {
		const values = getValues()
		const customizations = parseCustomizations(values?.customizations)
		setModal({
			...modal,
			dialogClassName: styles?.customModalStyle,
			title: 'Customization Options',
			visible: true,
			size: 'lg',
			Component: (
				<CustomizationForm
					ingredients={ingredients}
					register={register}
					customizationsObject={customizations}
					stateCustomizations={Array.isArray(customizations?.custom) ? customizations.custom : []}
					setValue={setValue}
					selectedGlobalCustomizations={selectedCustomizations}
					Restaurant={Restaurant}
					dish={dish}
					onSaveDishCustomizations={commitDishCustomizationsFromModal}
					{...modal}
				/>
			),
		})
	}

	return (
		<div>
			<Modal style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} {...modal} />
			<Form onSubmit={handleSubmit(submit)}>
				<Form.Group>
					<Form.Label>Dish name</Form.Label>
					<Form.Control
						defaultValue={update ? dish?.name : ''}
						ref={register({ required: 'Dish name cannot be empty' })}
						name="name"
						placeholder="Dish Name"
					/>
					{(errors?.name?.message || error) && (
						<small className={registerStyle.registerErrorText}>{errors?.name?.message || error}</small>
					)}
				</Form.Group>
				<Form.Group>
					<Form.Label>Dish description</Form.Label>
					<TextEditor placeholder="Dish Description" value={editorValue} onChange={setEditorValue} />
					{errors?.description?.message && (
						<small className={registerStyle.registerErrorText}>{errors?.description?.message}</small>
					)}
				</Form.Group>

				<Form.Group className="d-flex mobile-direction-column has-gap">
					<Form.Group>
						<Form.Label>Dish Calories</Form.Label>
						<Form.Control
							defaultValue={update ? dish?.calories : ''}
							ref={register({
								min: { value: 0, message: 'Calories can not be a negative number!' },
							})}
							min={0}
							type="number"
							name="calories"
							placeholder="Calories"
						/>
						{errors?.calories?.message && (
							<small className={registerStyle.registerErrorText}>{errors?.calories?.message}</small>
						)}
					</Form.Group>
					<Form.Group>
						<Form.Label>Dish Price</Form.Label>
						<Form.Control
							defaultValue={update ? (dish?.price ? dish?.price : '') : ''}
							ref={register}
							type="text"
							name="price"
							placeholder="Price"
						/>
						{priceError && <small className={registerStyle.registerErrorText}>{priceError}</small>}
					</Form.Group>
					<Form.Group>
						<Form.Label>Alcohol Percentage</Form.Label>
						<Form.Control
							defaultValue={update ? dish?.alcoholPercentage : ''}
							min={0}
							max={100}
							ref={register({
								min: { value: 0, message: 'Alcohol Percentage can not be a negative number!' },
								max: { value: 100, message: 'Alcohol Percentage can not be greater than 100!' },
							})}
							type="number"
							name="alcoholPercentage"
							placeholder="Alcohol Percentage"
						/>
						{errors?.alcoholPercentage?.message && (
							<small className={registerStyle.registerErrorText}>
								{errors?.alcoholPercentage?.message}
							</small>
						)}
					</Form.Group>
				</Form.Group>

				<DietaryConsiderations
					key={dietaryResetKey}
					register={register}
					setValue={setValue}
					update={update}
					Dish={dish}
					Restaurant={restaurant}
					Admin={Admin}
				/>

				<Form.Group>
					<Form.Label>Select menu-wide customizations</Form.Label>
					<SelectList
						placeholder="Select menu-wide customizations"
						isMulti={true}
						value={transformCustomizations(selectedCustomizations, true)}
						options={transformCustomizations(restaurant?.customizations)}
						onChange={(data) => {
							setSelectedCustomizations(data)
							mergeSelectedCustomsIntoForm(data)
						}}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Add dish specific customizations</Form.Label>
					<SelectList
						placeholder="Add dish specific customizations"
						isMulti={true}
						value={transformCustomizations(getCustomCustomizations())}
						options={[]}
						onChange={showCustomizationModal}
						onMenuOpen={showCustomizationModal}
					/>
				</Form.Group>

				<SideDishesSelector
					categories={categories}
					sideDishes={sideDishes}
					selectedSideDishes={selectedSideDishes}
					setSelectedSideDishes={setSelectedSideDishes}
					sideDishesLabel={restaurant?.sideDishesLabel}
				/>
				<Form.Group style={{ marginTop: 40 }}>
					<IngredientAttribution
						addIngredient={addIngredient}
						Auth={Auth}
						ingredients={ingredients}
						selectedIngredients={selectedIngredients}
						setSelectedIngredients={setSelectedIngredients}
					/>
				</Form.Group>
				<Form.Group>
					<Form.Label>Dish Image</Form.Label>
					<ImageInput
						key={dietaryResetKey}
						displayFile={update ? dish?.image : imageSelected}
						modalStyle={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
						uploadFromMedia={true}
						defaultValue={update ? dish?.image : ''}
						placeHolderImage={update && dish?.image ? dish?.image : null}
						handleImage={(img) => setImageSelected(img)}
						onImageRemove={() => setImageSelected(null)}
					/>
					{fileError && <small className={registerStyle.registerErrorText}>{fileError}</small>}
				</Form.Group>
				<Form.Group>
					<Form.Label>Dish Available</Form.Label>
					<Checkbox
						checked={isAvailable}
						onChange={(e) => {
							setIsAvailable(e.target.checked)
						}}
					/>
				</Form.Group>
				<ModalFooter>
					<Button
						disabled={Object.keys(errors).length > 0 || loading ? true : false}
						onClick={handleSubmit(submit)}
					>
						{loading ? <LoadingSpinner size="sm" /> : update ? 'Update Dish' : 'Create Dish'}
					</Button>
					{typeof closeModal === 'function' && (
						<Button variant="secondary" onClick={closeModal}>
							Close
						</Button>
					)}
				</ModalFooter>
			</Form>
		</div>
	)
}

const mapStateToProps = (state) => ({
	Dish: state?.Dish,
	Ingredient: state?.Ingredient,
	Auth: state?.Auth,
	Restaurant: state?.Restaurant,
	Category: state?.Category,
	Admin: state?.Admin,
})
export default connect(mapStateToProps)(DishForm)
